import { createFileRoute, Link } from '@tanstack/react-router'
import { APP_NAME } from '@/lib/utils'
import { LegalPage, LegalSection } from '@/components/layout/legal-page'

const LAST_UPDATED = 'May 18, 2026'

export const Route = createFileRoute('/terms')({
  component: TermsOfServicePage,
  head: () => ({
    meta: [
      {
        title: `${APP_NAME} | Terms of Service`,
      },
    ],
  }),
})

function TermsOfServicePage() {
  return (
    <LegalPage
      title='Terms of Service'
      lastUpdated={LAST_UPDATED}
      description={`These Terms of Service govern your access to and use of ${APP_NAME}, including the delivery management tools and related services.`}
    >
      <LegalSection title='Acceptance of terms' delayClass='delay-100'>
        <p>
          By using ${APP_NAME}, you agree to these terms and confirm that you
          have authority to accept them on behalf of your organization.
        </p>
      </LegalSection>

      <LegalSection title='Service scope' delayClass='delay-150'>
        <p>
          ${APP_NAME} provides tools for creating shipments, assigning delivery
          tasks, tracking progress, and generating proof of delivery. Delivery
          windows and transit times are estimates and may vary due to factors
          outside our control.
        </p>
      </LegalSection>

      <LegalSection title='Account responsibilities' delayClass='delay-200'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>Keep login credentials confidential and secure.</li>
          <li>Provide accurate and complete information for shipments.</li>
          <li>Use the platform only for authorized business purposes.</li>
        </ul>
      </LegalSection>

      <LegalSection title='Shipment responsibilities' delayClass='delay-250'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>Ensure items are lawful, properly packaged, and labeled.</li>
          <li>
            Provide correct pickup and delivery addresses and contact data.
          </li>
          <li>Comply with applicable regulations for restricted goods.</li>
        </ul>
      </LegalSection>

      <LegalSection title='Fees and payments' delayClass='delay-300'>
        <p>
          Fees are presented in ${APP_NAME} or in your contract. You agree to
          pay all applicable charges and taxes. Late payments may result in
          account suspension until balances are resolved.
        </p>
      </LegalSection>

      <LegalSection title='Cancellations and changes' delayClass='delay-350'>
        <p>
          Requests to cancel or modify a shipment should be made before pickup
          or dispatch. Changes after dispatch may incur additional fees.
        </p>
      </LegalSection>

      <LegalSection title='Prohibited use' delayClass='delay-400'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>
            Use ${APP_NAME} for unlawful, harmful, or fraudulent activity.
          </li>
          <li>Interfere with platform security or service availability.</li>
          <li>Reverse engineer or misuse the platform or its data.</li>
        </ul>
      </LegalSection>

      <LegalSection
        title='Disclaimers and limitation of liability'
        delayClass='delay-450'
      >
        <p>
          ${APP_NAME} is provided on an "as is" and "as available" basis. To the
          maximum extent permitted by law, we disclaim warranties and limit
          liability for indirect, incidental, or consequential damages.
        </p>
      </LegalSection>

      <LegalSection title='Changes to these terms' delayClass='delay-500'>
        <p>
          We may update these terms from time to time. Continued use of $
          {APP_NAME} after changes means you accept the updated terms.
        </p>
      </LegalSection>

      <LegalSection title='Contact' delayClass='delay-550'>
        <p>
          For questions about these terms, contact your account manager or the
          support team through the ${APP_NAME} platform.
        </p>
      </LegalSection>

      <div className='text-muted-foreground animate-in fade-in-0 slide-in-from-bottom-4 text-sm delay-600 duration-700'>
        Related:{' '}
        <Link to='/privacy' className='text-primary underline'>
          Privacy Policy
        </Link>
      </div>
    </LegalPage>
  )
}
